import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { haptic } from '@/telegram';

interface ToastOptions {
  text: ReactNode;
  /** Кнопка справа: «Отменить». */
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  show: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_MS = 5000;

/**
 * Одно всплывающее сообщение над навигацией. Главное назначение — «Отменить»
 * после быстрой траты: подтверждать каждую трату диалогом долго, а ошибиться
 * суммой легко, поэтому вместо вопроса «вы уверены?» — отмена задним числом.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toast, setToast] = useState<(ToastOptions & { id: number }) | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((options: ToastOptions) => {
    window.clearTimeout(timer.current);
    setToast({ ...options, id: Date.now() });
    timer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Живая область смонтирована всегда: в новой области скринридеры
          первое сообщение часто не зачитывают. */}
      <div
        className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4"
        style={{ bottom: 'calc(var(--nav-height) + var(--nav-gap) + var(--safe-bottom) + 0.75rem)' }}
        role="status"
      >
        {toast ? (
          <div
            key={toast.id}
            className="pointer-events-auto flex w-full max-w-md animate-toast-in items-center gap-3 rounded-xl bg-[var(--toast-bg)] py-2 pr-2 pl-4 text-[14px] text-background shadow-[0_12px_32px_-12px_rgba(0,0,0,0.45)]">
            <div className="min-w-0 flex-1 truncate">{toast.text}</div>
            {toast.action ? (
              <button
                type="button"
                className="shrink-0 rounded-xl bg-background/15 px-3 py-2 text-[14px] font-semibold active:opacity-60"
                onClick={() => {
                  haptic.tap();
                  toast.action?.onClick();
                  window.clearTimeout(timer.current);
                  setToast(null);
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
